import CryptoJS from 'crypto-js';

// .env values follow Angular's style (quoted, trailing comma) — strip them.
const stripEnvValue = (val: string | undefined): string =>
  (val ?? '').replace(/^['"]/, '').replace(/['"],?$/, '');

// Shared secret MUST match the Angular menu-link encryption key:
//   CryptoJS.AES.encrypt(userName, 'SecretKeyEmis@2025')
// This is OpenSSL passphrase mode (NOT the key/IV CBC scheme in cryptoService).
const SSO_SECRET =
  stripEnvValue(process.env.REACT_APP_SSO_secret) || 'SecretKeyEmis@2025';

// CryptoJS passphrase-mode output is always base64 of "Salted__" → "U2FsdGVkX1..."
// We use it as a reliable marker to separate the leading type digit(s) from the
// ciphertext in the path form (e.g. "/2U2FsdGVkX1...").
const OPENSSL_MARKER = 'U2FsdGVkX1';

export interface SsoPayload {
  /** login type: "2" = tnemis (from the Angular menu link prefix) */
  type: string;
  /** AES (passphrase-mode) ciphertext of the username */
  cipher: string;
}

type LocationLike = Pick<Location, 'pathname' | 'search'>;

/**
 * Extracts the SSO handoff payload from the current URL, if present.
 *
 * Supports both forms the Angular app can emit:
 *  - Path form (menu links): `/<type><cipher>`  e.g. `/2U2FsdGVkX1...`
 *  - Query form (handleSSOLogin): `?userLogin=<cipher>&type=<type>`
 *
 * Returns null when the URL carries no SSO payload (normal app navigation).
 */
export function extractSsoPayload(
  loc: LocationLike = window.location
): SsoPayload | null {
  // 1) Query form: ?userLogin=<cipher>
  const params = new URLSearchParams(loc.search);
  const q = params.get('userLogin');
  if (q) {
    return { type: params.get('type') ?? '2', cipher: q };
  }

  // 2) Path form: /<type><cipher>. The ciphertext (base64) may itself contain
  //    "/", so we can't split on segments — anchor on the OpenSSL marker.
  const raw = loc.pathname.replace(/^\/+/, '');
  const idx = raw.indexOf(OPENSSL_MARKER);
  if (idx === -1) return null;

  return {
    type: raw.slice(0, idx) || '2',
    cipher: raw.slice(idx),
  };
}

/**
 * Decrypts the username from the SSO ciphertext using passphrase-mode AES,
 * matching Angular's `CryptoJS.AES.encrypt(userName, 'SecretKeyEmis@2025')`.
 * Returns an empty string when decryption fails.
 */
export function decryptSsoUsername(cipher: string): string {
  try {
    const bytes = CryptoJS.AES.decrypt(cipher, SSO_SECRET);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
}

/**
 * Encrypts a username with the SAME passphrase-mode AES scheme the inbound SSO
 * link uses (`CryptoJS.AES.encrypt(userName, 'SecretKeyEmis@2025')`). This is
 * the outbound side of the handoff: the ciphertext is appended to an external
 * portal URL (SLAS / SMC) so that portal can decrypt the username and sign the
 * user in — mirroring how this app reads an inbound `/<type><cipher>` link.
 */
export function encryptSsoUsername(userName: string): string {
  return CryptoJS.AES.encrypt(userName, SSO_SECRET).toString();
}

/**
 * Builds the SSO handoff URL to an external EMIS portal, matching exactly how
 * the Angular menu link concatenates it:
 *
 *   ENV_CONFIG.smcSiteURL + 2 + encryptedUrName
 *
 * i.e. `<siteURL><type><cipher>` with the RAW base64 ciphertext appended — NOT
 * URL-encoded. The portals read the cipher straight off the URL, so encoding
 * it (turning "+" → "%2B", "=" → "%3D", "/" → "%2F") corrupts the handoff and
 * the portal fails to decrypt. `type` defaults to "2" (tnemis).
 */
export function buildSsoRedirectUrl(
  siteURL: string,
  userName: string,
  type = '2'
): string {
  const cipher = encryptSsoUsername(userName);
  return `${siteURL}${type}${cipher}`;
}
